<script lang="ts">
    import PanelsPage from '@Evaluation/Estimations/Pages/PanelsPage'
    export default PanelsPage
</script>

<template>
    <div>
        <EstimationsPanelEditor v-if="panelEditor" v-bind="panelEditor"
                                @createPanel="handleCreatePanelCommand"
                                @updatePanel="handleUpdatePanelCommand"
                                @deletePanel="handleDeletePanelCommand"
                                @close="closePanelEditor" />

        <AttributesSelector v-if="attributesManager != null"
                            v-bind="attributesManager"
                            @close="closeAttributesManager"
                            @save="handleUpdatePanelAttributesSettingsCommand" />

        <PanelAlternativesManager :viewModel="panelAlternativesManager"
                                @save="handleUpdatePanelAlternativesSettingsCommand" />

        <PanelOpener :viewModel="panelOpener" @open-panel="handleOpenPanelAccessCommand" @show-links="showAccessLinks" />

        <SurveyAccessLinks :viewModel="surveyAccessLinks" 
                           @send-email="handleSendEmailCommand" 
                           @download-links="downloadLinks" />

        <PanelCloser :viewModel="panelCloser" 
                     @close-access="handleClosePanelAccessCommand" />


        <div style="padding-top: 130px;">
            <PortfolioNavigation :workspaceId="workspaceId" :projectId="projectId" />

            <div class="container">
                <div class="row">
                    <div class="col-lg-6">
                        <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Assessments</h1>
                    </div>
                    <div class="col-lg-6 text-right">
                        <div class="dropdown d-inline-block">
                            <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown" style="padding: 0.7em 2em;">
                                Export to xls
                            </button>
                            <div class="dropdown-menu">
                                <a class="dropdown-item" @click="downloadVotes">Votes</a>
                                <a class="dropdown-item" @click="downloadDisagreements">Disagreements</a>
                            </div>
                        </div>

                        <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showSurveyCreator">Create panel</button>

                        <a class="btn btn-outline-secondary btn-lg" href="https://www.transparentchoice.com/user-guide/scoring" title="User guide: Score projects" target="_blank">
                            <i class="fa fa-info" aria-hidden="true"></i>
                        </a>

                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="container">
                <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h3>

                <div v-if="initialized">
                    <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">
                        <form class="form-inline my-2 my-lg-0 mr-2" role="search">
                            <div class="input-group input-group-sm">
                                <input class="form-control form-control-sm" type="search" placeholder="Search" aria-label="Search" v-model.trim="SearchPhrase">
                                <span class="input-group-append">
                                    <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                                </span>
                            </div>
                        </form>

                        <form class="form-inline my-2 my-lg-0">
                            <div class="dropdown">
                                Filter by: <a class="text-primary font-weight-bold dropdown-toggle" data-toggle="dropdown">{{FilterByOwnerLabel}}</a>
                                <div class="dropdown-menu" style="max-height: 250px; overflow-y: auto;">
                                    <a class="dropdown-item my-2" @click="setOwnerFilter(null)">
                                        <span>All owners</span>
                                    </a>
                                    <a v-for="m in WorkspaceMembers" class="dropdown-item my-2" @click="setOwnerFilter(m.Email)">
                                        <span>{{m.Name}}</span><br />
                                        <small>{{m.Email}}</small><br />
                                    </a>
                                </div>
                            </div>
                        </form>

                    </nav>
                    <br />


                    <div class="row">
                        <div v-for="s in SurveysView" :key="s.SurveyId" class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                            <div class="card mt-2">
                                <div class="card-body">
                                    <h5 class="card-title">
                                        <i class="fa fa-lock text-danger" title="Survey is closed" v-if="!s.SurveyStarted"></i>
                                        <i class="fa fa-unlock-alt text-success" title="Survey is open" v-if="s.SurveyStarted"></i>
                                        <span>{{s.Name}}</span><br />
                                        <small class="text-muted">Owner: {{getOwnerName(s)}}</small>
                                    </h5>

                                    <p class="card-text" v-if="s.IsSaving"><i class="fa fa-spinner fa-spin"></i> Saving....</p>

                                    <div v-if="!s.IsSaving">
                                        <div class="row">
                                            <div class="col text-nowrap">
                                                <a class="text-primary" v-sanitizedHref="`/#/pp/assessments-panelists/${workspaceId}/${projectId}/${s.SurveyId}`">
                                                    Panelists: {{s.RespondentsCount}}
                                                </a>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col text-nowrap">
                                                <a class="text-primary" @click="showAttributesManager(s)">
                                                    Attributes: {{s.AttributesCount}}/{{TotalCriteriaCount}}
                                                </a>
                                            </div>
                                        </div>

                                        <div class="row">
                                            <div class="col text-nowrap">
                                                <a class="text-primary" @click="showPanelAlternativesManager(s)">
                                                    {{AlternativesLabel}}: {{s.AlternativesViewName}}
                                                </a>
                                            </div>
                                        </div>
                                        <br>

                                        <a v-sanitizedHref="`/#/pp/assessments-review/${workspaceId}/${projectId}/${s.SurveyId}`" class="btn btn-sm btn-outline-secondary">Review</a>
                                        <a @click="handleUpdateAlternativesCommand(s)" class="btn btn-sm btn-outline-secondary">Save scores</a>

                                        <div class="dropdown d-inline-block">
                                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                                Actions
                                            </button>
                                            <div class="dropdown-menu">
                                                <a class="dropdown-item" @click="showSurveyEditor(s)">Edit</a>
                                                <a class="dropdown-item" @click="handleClonePanelCommand(s)">Clone</a>
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" v-if="!s.SurveyStarted" @click="showPanelOpener(s.SurveyId)">Open survey</a>
                                                <a class="dropdown-item" v-if="s.SurveyStarted" @click="showPanelCloser(s.SurveyId)">Close survey</a>
                                                <div class="dropdown-divider"></div>
                                                <a class="dropdown-item" @click="deletePanel(s)">Delete</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>


</template>
